import { Avatar, Button, Icon } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { AvatarCropper } from 'lib-shared-legacy-components';
import { Organization, User } from 'lib-shared-types-api';
import { PropsWithClassName } from 'lib-shared-types-utils';
import { createRef, PureComponent } from 'react';

import { ConfirmModal } from '../../confirmModal/components/confirmModal';
import CropUpload from '../../crop/CropUpload';
import styles from '../styles/avatarControl.module.scss';

interface AvatarControlProps extends PropsWithClassName {
  user?: User | User | Organization | null;
  type?: 'user' | 'org';
  onSelectAvatar?: (dataURI: string) => void;
  onDeleteAvatar?: () => void;
}

type StateT = {
  isConfirmDeleteOpen: boolean;
  cropperOpen: boolean;
  img: string | null;
};

export default class AvatarControl extends PureComponent<
  AvatarControlProps,
  StateT
> {
  static defaultProps = {
    type: 'user',
  };

  inputRef: { current: null | CropUpload };

  constructor(props: AvatarControlProps) {
    super(props);

    this.handleFileChange = this.handleFileChange.bind(this);
    this.handleRequestHide = this.handleRequestHide.bind(this);
    this.handleCrop = this.handleCrop.bind(this);
    this.deleteAvatar = this.deleteAvatar.bind(this);
    this.changeAvatar = this.changeAvatar.bind(this);
    this.confirmDeleteAvatar = this.confirmDeleteAvatar.bind(this);
    this.hideConfirm = this.hideConfirm.bind(this);

    this.inputRef = createRef();

    this.state = {
      isConfirmDeleteOpen: false,
      cropperOpen: false,
      img: null,
    };
  }

  handleFileChange(dataURI: string) {
    this.setState({
      img: dataURI,
      cropperOpen: true,
    });
  }

  handleRequestHide() {
    this.setState({ cropperOpen: false });
  }

  handleCrop(dataURI: string): void {
    this.setState({
      cropperOpen: false,
      img: null,
    });

    if (this.props.onSelectAvatar) {
      this.props.onSelectAvatar(dataURI);
    }
  }

  deleteAvatar(): void {
    this.props.onDeleteAvatar?.();
    this.hideConfirm();
  }

  changeAvatar = () => {
    if (this.inputRef.current && this.inputRef.current.fileInput.current) {
      const input = this.inputRef.current.fileInput.current;
      input.click();
    }
  };

  hideConfirm() {
    this.setState({
      isConfirmDeleteOpen: false,
    });
  }

  confirmDeleteAvatar() {
    this.setState({
      isConfirmDeleteOpen: true,
    });
  }

  shouldRenderDeleteModal(): boolean {
    return !!(this.props.user?.avatar_url && this.props.onDeleteAvatar);
  }

  render() {
    const { user, className, type } = this.props;
    const avatarType = type === 'org' ? 'logo' : 'avatar';
    const rootClass = classnames(styles.avatarRow, className);

    return (
      <div className={rootClass}>
        <div className={styles.avatarPhotoHolder}>
          <div className={styles.avatarPhoto}>
            <CropUpload
              ref={this.inputRef}
              handleFileChange={this.handleFileChange}
            />

            <div className={styles.avatarEdit}>
              <Icon name="plus" className={styles.icon} />
            </div>

            <Avatar user={user} />
          </div>

          {this.state.cropperOpen === true && (
            <AvatarCropper
              onRequestHide={this.handleRequestHide}
              cropperOpen={this.state.cropperOpen}
              onCrop={this.handleCrop}
              image={this.state.img || ''}
            />
          )}
        </div>

        <Button
          variant="secondary"
          className={styles.avatarRowButton}
          onClick={this.changeAvatar}
        >
          {user?.avatar_url ? `Change ${avatarType}` : `Upload ${avatarType}`}
        </Button>

        {this.shouldRenderDeleteModal() && (
          <>
            <Button
              variant="secondary"
              className={styles.avatarRowButton}
              onClick={this.confirmDeleteAvatar}
            >
              {`Reset ${avatarType}`}
            </Button>

            <ConfirmModal
              isShown={this.state.isConfirmDeleteOpen}
              headline="Are you sure?"
              text={`Are you sure you want to reset the ${avatarType}?`}
              confirmText="Yes, reset it now"
              cancelText="Cancel"
              confirm={this.deleteAvatar}
              cancel={this.hideConfirm}
            />
          </>
        )}
      </div>
    );
  }
}
